import {
  getCarContextData,
  getCarCardContextData,
  getPageData,
  getInterActiveElementText,
  hasEventCarContext,
  isInterActiveElement,
  additionalFormData,
  getClosestPageSection,
  formCarSearchEvent

} from './analytics-helper';
import callOnce from './utils/call-once';
import debounce from './utils/debounce.js';

/**
 * Google Analytics 4 datalayer
 *
 * Note: Google Analytics window.ga object is not initialized if user has not accepted "Tilastollisia evästeitä" TODO: Pitääkö tämä paikkansa?
 * in Cookie Banner.
 */
class GA4 {
  /**
   * Google Analytic measurement identifier
   */
  gaMeasurementId = 'G-M12D5H34EK';

  /**
   * Google Analytic API secret key
   */
  gaApiSecret = 'I8LaD8CRQhmfTNIPlo6KEg';

  /**
   * Client identifier
   */
  clientId;

  constructor() {
    this._delayedPushEvents = debounce(this.pushEvents.bind(this), 500);
  }

  /**
   * @param {string} apiSecret Google analytic api secret from environment variables
   */
  set apiSecret(apiSecret) {
    this.gaApiSecret = apiSecret;
  }

  /**
   * Return apiSecret
   *
   * @returns {string} apiSecret
   */
  get apiSecret() {
    return this.gaApiSecret;
  }

  /**
   * @param {string} gaMeasurementId data stream id from environment variables
   */
  set measurementId(gaMeasurementId) {
    this.gaMeasurementId = gaMeasurementId;
  }

  /**
   * Return measurements id
   *
   * @returns {string} measurementsId
   */
  get measurementId() {
    return this.gaMeasurementId;
  }

  /**
   * Push event object to datalayer
   *
   * @param {object[]|object} events data event objects
   */
  async sendEvent(events = []) {
    if (!Array.isArray(events)) {
      events = [events];
    }

    if (!('customDataLayer' in window)) {
      window.customDataLayer = [];
    }

    events.forEach(event => {

      if( !event.params ) {
        event.params = {};
      }

      event.params['page_url'] = window?.location?.href;

      // if url contains session_fbclid, add it to event params
      const fbclid = window.location.href.match(/session_fbclid=([^&]+)/);
      if (fbclid) {
        event.params['session_fbclid'] = fbclid[1];
      }

      //if url contains sessioj_gclid, add it to event params
      const gclid = window.location.href.match(/session_gclid=([^&]+)/);
      if (gclid) {
        event.params['session_gclid'] = gclid[1];
      }

      // Inject car context data if available

      if( hasEventCarContext(event.name) ) {
        const carData = getCarContextData();

        if( carData ) {
          event.params = { ...event.params, ...carData };
        }
      }

      const pageData = getPageData();

      if( pageData ) {
        event.params = { ...event.params, ...pageData };
      }

    });


    window.customDataLayer = [ ...window.customDataLayer, ...events ];

    //push event to datalayer for marketing purposes
    this.pushGAevent(events[0]);

    //push events to Google Analytics API
    this._delayedPushEvents();

  }

  /**
   * Post events to Google Analytics API.
   */
  async pushEvents() {

    if( window.customDataLayer && window.customDataLayer.length > 0 ) {
      let baseUrl;

      //If ANALYTIC_MODE is 'debug' events are sended to debug server
      //More about it here: https://developers.google.com/analytics/devguides/collection/protocol/ga4/validating-events?client_type=gtag
      if( window.ANALYTIC_MODE === 'debug') {
        baseUrl = 'https://www.google-analytics.com/debug/mp/collect'
      } else {
        baseUrl = 'https://www.google-analytics.com/mp/collect'
      }

      const url = `${baseUrl}?measurement_id=${this.gaMeasurementId}&api_secret=${this.gaApiSecret}`;

      try {
        await fetch(url, {
          method: 'POST',
          body: JSON.stringify({
            client_id: this.getClientID(),
            events: [ ...window.customDataLayer ]
          })
        });

        //Empty sended events.
        window.customDataLayer = [];
      } catch (error) {
        console.info('Problem sending GA4 events forward...', error);
      }
    }
  }

  /**
   * Attach event listeners, send page visit event and so on. Performs routines
   * that need to be done after page load.
   */
  init() {
    this.attachEventListeners();
    //this.sendPageVisit(); //these are not in specs, but we might need them later
    //this.sendFirstVisit(); //these are not in specs, but we might need them later

    /*
    * For contact form thanks page we need to send event only once.
    * If user refreshes the page we need to prevent sending event again.
    */
    if(window.location.pathname.includes('ota-yhteytta/kiitos') ) {
      const hash = window.location.hash;
      history.pushState('', document.title, window.location.pathname + window.location.search);

      if (hash && localStorage.getItem('formHash') === null) {
        localStorage.setItem('formHash', hash);

        this.sendEvent({
          name: 'form_submit',
          params: {
            'form_type': 'Contact form',
            'page_section': 'main',
            'contact_type': 'form - Contact form',
            'page_type':  'contact',
          }
        });
      }
    } else {
      /* if we are outside of thanks page, clear local storage */
      if (!window.location.pathname.includes('kiitos')) {
        localStorage.removeItem('formHash');
      }
    }
  }

  /**
   * Return client identifier
   *
   * @returns {string|undefined} Google Analytic client ID
   */
  getClientID() {
    if (!window.ga || !window.ga.getAll || this.clientId) {
      return this.clientId;
    }

    window.ga.getAll().forEach(tracker => {
      this.clientId = tracker.get('clientId');
    });

    return this.clientId;
  }

  /**
   * Handle click event are taken from all interactive elements like
   * input, textareas, buttons and links.
   * @param {string} name event name
   * @param {MouseEvent} event event object
   * @param {object} params additional parameters
   */
  handleClickEvent(name, event, params) {

    if( isInterActiveElement(event?.target) ) {

      this.sendEvent({
        name: name,
        params: {
          'click_text': getInterActiveElementText(event.target),
          'page_section': getClosestPageSection(event.target),
          'click_url': event.target?.href ?? '',
          ...params
        }
      });
    }
  }


  /**
   * Attach document object event listeners.
   */
  attachEventListeners() {
    this.attachScrollListener();


    /**
     * Form submits
     */
    document.body.querySelectorAll('form').forEach( formElement => {
      formElement.addEventListener('submit', (event) => {

        const params =  additionalFormData(event);

        if (event.target.dataset.gtmEventFormType === 'Vehicle listing page search') {
          this.sendEvent(formCarSearchEvent(event.target, params));
          return;
        }

        const pattern = new RegExp('/ota-yhteytta/[^/]+');

        // If form is a contact form for specific car, send event with car context data
        if(event.target?.stage_form_id?.value === 'mAglLMLTRS6UBpfgi8FFlQ' && pattern.test(window.location.pathname )) {

          const carData = getCarContextData();
          const carContactFormParams =
          {
            'form_information': {
              'form_type': 'Contact form',
              'page_section': 'main',
              'contact_type': 'form - Contact form',
              'page_type':  'contact',
            },
            'vehicle_information': {...carData}
          };

          this.sendEvent({
            name: 'form_submit',
            params:  { ...carContactFormParams }
          });

          return;
        }

        if( event.target instanceof HTMLFormElement ) {
          // Kontaktilomake, not sending analytics from form
          if(event.target?.stage_form_id?.value === 'mAglLMLTRS6UBpfgi8FFlQ') {
            return;
          }

          //front page hero vehicle search form
          if (event.target.dataset.gtmEventFormType === 'Hero vehicle search form') {
            if (window.vehicleSearchFormSubmitted) {
              this.sendEvent(formCarSearchEvent(event.target, params));
            }
            return;
          }

          //general form submit event
          this.sendEvent({
            name: 'form_submit',
            params:  { ...params }
          });
        }
      });
    })

    /**
     * Click events
     */
    document.body.addEventListener('click', (event) => {
      const {target} = event;

      event.stopPropagation();

      //handle click event only if target is interactive element
      if( target?.tagName === 'A' || target?.tagName === 'BUTTON' && target?.type !== 'submit' && !target.classList.contains('multiselect-button')) {

        //Get car context data if available for current event
        //look variables in analytics-helper.js

        const params = (hasEventCarContext(target?.dataset['gtmEventAction'])) ? getCarCardContextData(target?.dataset['gtmEventAction'], target.dataset) : {};

        //handle click event based on event action name or default to 'click_cta'
        this.handleClickEvent((target?.dataset['gtmEventAction']) ? target.dataset['gtmEventAction'] : 'click_cta', event, params);
      }

      // Vehicle listing page search form
      // we dont want to send all submits to analytics so we send it only when user click outside the form and if the form has been submitted atleast once
      const vehicleSearchForm = document.querySelector('#vehicle-search-form');

      if (vehicleSearchForm && !vehicleSearchForm.contains(event.target) && window.vehicleSearchFormSubmitted) {
        const params = additionalFormData(vehicleSearchForm);
        this.sendEvent(formCarSearchEvent(vehicleSearchForm, params));
      }

    });
  }

  /**
   * Attach scroll event listener to document
   */
  attachScrollListener() {
    const sendEventOnce = callOnce(() => { this.sendEvent({ name: 'scroll' , params: { depth: 90 } }) })

    document.addEventListener('scroll', () => {
      const scrollPercentage = parseInt( 100 * (window.pageYOffset / ( document.body.scrollHeight - document.body.clientHeight )));
      if( scrollPercentage >= 90 ) {
        sendEventOnce();
      }
    });
  }

  /**
   * This event is send only when a user visit page first time.
   */
  sendFirstVisit() {
    if( window.localStorage.getItem('ga4_first_view') !== 'true' ) {
      this.sendEvent({ name: 'first_view'})
      window.localStorage.setItem('ga4_first_view', 'true');
    }
  }

  /**
   * This event is send every page visit
   *
   * @param {object} params parameters passed to event object, something like car page context object
   */
  sendPageVisit( params={} ) {
    this.sendEvent({ name: 'page_view', params: { ...params } });
  }

  /**
   * push event to dataLayer
   * @param {object} obj event object
   */

  pushGAevent(obj) {

    window.dataLayer = window.dataLayer || [];

    const event = {
      name: obj.name,
      ...obj.params,
    };

    window.dataLayer.push( event );
  }

}

export { GA4 };
export default new GA4();
